import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Row, Col, Button, ButtonGroup, Form, Alert, Modal } from "react-bootstrap";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";
import ConfirmModal from "../Confirm/ConfirmModal";
import { useConfirm } from "../Confirm/useConfirm";
import { useNotification } from "../../NotificationContext";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import {
  updateSessionById,
  uploadImage,
  fetchPracticeTemplates,
  fetchTemplateVersion,
  exportSessionTemplatePdf,
  emailSessionTemplatePdf,
  getPatientById,
  getPracticesByNumbers,
  updateAppointmentStatusByPatientId,
} from "../../api";
import { getFinComms } from "../../afinanceapi";
import { getPeopleForUser } from "../../apeople";
import NoteRenderer from '../Templates/NoteRenderer';
import { notesToPages } from '../Templates/TemplateBuilder';
import SyncToButton from '../SyncTo/SyncToButton';
import { getUserPreferences } from '../BootNav/userPreferences';
import ReactQuill from "react-quill";
import ImageAnnotator from "./EditImages";
import {
  FaStop,
} from "react-icons/fa";
import { ReactSketchCanvas } from "react-sketch-canvas";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { getBookingsReturnPath } from "../../utils/bookingsReturn";
import "./EditSession.css";
import "../Communications/Comms.css";

// All non-empty email addresses on a person record (primary first).
const personEmails = (p) =>
  ["email", "email2", "email3", "email4", "email5"]
    .map((f) => (p?.[f] ?? "").trim())
    .filter(Boolean);

const personDisplayName = (p) =>
  `${p?.title ?? ""} ${p?.firstName ?? ""} ${p?.lastName ?? ""}`
    .replace(/\s+/g, " ")
    .trim();

// --- Voice recorder hook (modal-driven) ---
const useVoiceRecorder = () => {
  const { transcript, resetTranscript, listening } = useSpeechRecognition();
  const start = () => {
    if (!SpeechRecognition.browserSupportsSpeechRecognition()) return;
    resetTranscript();
    SpeechRecognition.startListening({
      continuous: true,
      interimResults: true,
      language: "en-US",
    });
  };
  const stop = () => {
    SpeechRecognition.stopListening();
    return (transcript || "").trim();
  };
  const clear = () => resetTranscript();
  return { listening, transcript, start, stop, clear };
};

// --- Recorder Modal ---
const RecorderModal = ({ show, onClose, onDone, vr }) => {
  useEffect(() => {
    if (show) vr.clear();
  }, [show]); // clear transcript on open

  const handleStart = () => vr.start();
  const handleStop = () => vr.stop();
  const handleUse = () => {
    const text = (vr.transcript || "").trim();
    if (vr.listening) vr.stop();
    onDone?.(text);
    onClose?.();
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Voice Recorder</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column align-items-center gap-3">
          <div className={`soundwave ${vr.listening ? "live" : ""}`}>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
          <div className="w-100">
            <Form.Label className="fw-semibold">Live transcript</Form.Label>
            <div
              aria-live="polite"
              style={{
                minHeight: 90,
                border: "1px solid rgba(0,0,0,.1)",
                borderRadius: 8,
                padding: "0.5rem 0.75rem",
                background: "#fafafa",
                whiteSpace: "pre-wrap",
              }}
            >
              {vr.transcript || (
                <span className="text-muted">Say something…</span>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <div className="d-flex gap-2">
          {!vr.listening ? (
            <Button variant="outline-primary" onClick={handleStart}>
              <i className="fa-light fa-microphone me-2"></i> Start
            </Button>
          ) : (
            <Button variant="danger" onClick={handleStop}>
              <FaStop className="me-2" /> Stop
            </Button>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUse}
            disabled={!vr.transcript?.trim()}
          >
            Use Transcript
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

// --- Handwriting Modal (fullscreen canvas for stylus/touch input) ---
const HandwritingModal = ({ show, onHide, onSave, existingImage }) => {
  const canvasRef = useRef(null);
  const [strokeColor, setStrokeColor] = useState("#000000");
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [isEraser, setIsEraser] = useState(false);
  const [isHighlighter, setIsHighlighter] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(null);

  // Load existing image as background when modal opens
  useEffect(() => {
    if (show && existingImage) {
      setBackgroundImage(existingImage);
    } else if (!show) {
      setBackgroundImage(null);
    }
  }, [show, existingImage]);

  const handleSave = async () => {
    if (canvasRef.current) {
      try {
        // Check if user drew any new strokes
        const paths = await canvasRef.current.exportPaths();
        const hasNewStrokes = paths && paths.length > 0;

        // If there's a background but no new strokes, keep the original unchanged
        if (backgroundImage && !hasNewStrokes) {
          onSave(backgroundImage);
          onHide();
          return;
        }

        // Export the strokes layer at native canvas dimensions
        const strokesDataUrl = await canvasRef.current.exportImage("png");

        // If there's a background image, merge it with the strokes
        if (backgroundImage && hasNewStrokes) {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          // Load strokes image first to get canvas dimensions
          const strokesImg = new Image();
          strokesImg.crossOrigin = "anonymous";

          await new Promise((resolve, reject) => {
            strokesImg.onload = resolve;
            strokesImg.onerror = reject;
            strokesImg.src = strokesDataUrl;
          });

          // Load background image
          const bgImg = new Image();
          bgImg.crossOrigin = "anonymous";

          await new Promise((resolve, reject) => {
            bgImg.onload = resolve;
            bgImg.onerror = reject;
            bgImg.src = backgroundImage;
          });

          // Use strokes canvas dimensions as the base
          canvas.width = strokesImg.width || 800;
          canvas.height = strokesImg.height || 600;

          // Calculate aspect-ratio-preserving dimensions for background
          const bgAspect = bgImg.width / bgImg.height;
          const canvasAspect = canvas.width / canvas.height;

          let drawWidth, drawHeight, offsetX, offsetY;

          if (bgAspect > canvasAspect) {
            // Background is wider - fit to width
            drawWidth = canvas.width;
            drawHeight = canvas.width / bgAspect;
            offsetX = 0;
            offsetY = (canvas.height - drawHeight) / 2;
          } else {
            // Background is taller - fit to height
            drawHeight = canvas.height;
            drawWidth = canvas.height * bgAspect;
            offsetX = (canvas.width - drawWidth) / 2;
            offsetY = 0;
          }

          // Draw background centered and aspect-ratio preserved (keep transparent)
          ctx.drawImage(bgImg, offsetX, offsetY, drawWidth, drawHeight);

          // Draw strokes on top at their native size (no scaling needed)
          if (strokesImg.width > 0 && strokesImg.height > 0) {
            ctx.drawImage(strokesImg, 0, 0);
          }

          // Export merged image
          const mergedDataUrl = canvas.toDataURL("image/png");
          onSave(mergedDataUrl);
        } else {
          // No background, just use the strokes
          onSave(strokesDataUrl);
        }

        onHide();
      } catch (err) {
        console.error("Failed to export canvas:", err);
      }
    }
  };

  const handleUndo = () => canvasRef.current?.undo();
  const handleRedo = () => canvasRef.current?.redo();
  const handleClear = () => canvasRef.current?.clearCanvas();

  const toggleEraser = () => {
    setIsEraser(!isEraser);
    setIsHighlighter(false);
    if (canvasRef.current) {
      canvasRef.current.eraseMode(!isEraser);
    }
  };

  const toggleHighlighter = () => {
    const next = !isHighlighter;
    setIsHighlighter(next);
    setIsEraser(false);
    if (canvasRef.current) canvasRef.current.eraseMode(false);
    if (next) {
      setStrokeColor("rgba(255,255,0,0.4)");
      setStrokeWidth(20);
    } else {
      setStrokeColor("#000000");
      setStrokeWidth(3);
    }
  };

  const colorOptions = ["#000000", "#0f3490", "#dc3545", "#198754", "#6c757d"];
  const strokeOptions = [2, 3, 5, 8];

  return (
    <Modal
      show={show}
      onHide={onHide}
      fullscreen
      className="handwriting-modal"
      backdrop="static"
    >
      <Modal.Header className="handwriting-modal-header py-2" style={{ backgroundColor: "#CFE2FF" }}>
        <div className="d-flex align-items-center gap-3 flex-wrap w-100">
          {/* Color picker */}
          <div className="d-flex align-items-center gap-1">
            {colorOptions.map((color) => (
              <button
                key={color}
                type="button"
                className={`color-btn ${strokeColor === color && !isEraser && !isHighlighter ? "active" : ""}`}
                style={{ backgroundColor: color }}
                onClick={() => {
                  setStrokeColor(color);
                  setIsEraser(false);
                  setIsHighlighter(false);
                  setStrokeWidth(3);
                  canvasRef.current?.eraseMode(false);
                }}
                aria-label={`Color ${color}`}
              />
            ))}
          </div>

          {/* Stroke width */}
          <div className="d-flex align-items-center gap-1">
            {strokeOptions.map((width) => (
              <button
                key={width}
                type="button"
                className={`stroke-btn ${strokeWidth === width && !isHighlighter ? "active" : ""}`}
                onClick={() => {
                  setStrokeWidth(width);
                  setIsHighlighter(false);
                }}
                aria-label={`Stroke width ${width}`}
              >
                <span
                  style={{ width: width * 2, height: width * 2 }}
                  className="stroke-preview"
                />
              </button>
            ))}
          </div>

          {/* Tools */}
          <div className="d-flex align-items-center gap-2">
            <Button
              variant={isHighlighter ? "warning" : "outline-primary"}
              size="sm"
              onClick={toggleHighlighter}
              title="Highlighter"
            >
              <i className="fa-solid fa-highlighter" />
            </Button>
            <Button
              variant={isEraser ? "primary" : "outline-primary"}
              size="sm"
              onClick={toggleEraser}
              title="Eraser"
            >
              <i className="fa-solid fa-eraser" />
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleUndo}
              title="Undo"
            >
              <i className="fa-solid fa-rotate-left" />
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleRedo}
              title="Redo"
            >
              <i className="fa-solid fa-rotate-right" />
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleClear}
              title="Clear all"
            >
              <i className="fa-solid fa-trash" />
            </Button>
          </div>

          {/* Actions */}
          <div className="d-flex align-items-center gap-2 ms-auto">
            <Button variant="outline-primary" size="sm" onClick={onHide}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave}>
              Save Drawing
            </Button>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="p-0 handwriting-canvas-container" style={{ overflowY: "auto" }}>
        <div style={{ width: "100%", height: "600vh", position: "relative" }}>
          <ReactSketchCanvas
            ref={canvasRef}
            strokeWidth={strokeWidth}
            strokeColor={isEraser ? "transparent" : strokeColor}
            canvasColor="transparent"
            backgroundImage={backgroundImage || ""}
            preserveBackgroundImageAspectRatio="xMidYMid meet"
            style={{
              width: "100%",
              height: "100%",
            }}
            allowOnlyPointerType="all"
            withTimestamp={true}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};

// --- Inline Write Section (canvas for handwriting inside the bordered layout) ---
const InlineWriteSection = forwardRef(({ onSave, existingImage, onExpand, isFullScreen }, ref) => {
  const canvasRef = useRef(null);
  const [strokeColor, setStrokeColor] = useState("#000000");
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [isEraser, setIsEraser] = useState(false);
  const [isHighlighter, setIsHighlighter] = useState(false);

  const colorOptions = ["#000000", "#0f3490", "#dc3545", "#198754", "#6c757d"];
  const strokeOptions = [2, 3, 5, 8];

  const toggleEraser = () => {
    const next = !isEraser;
    setIsEraser(next);
    setIsHighlighter(false);
    canvasRef.current?.eraseMode(next);
  };

  const toggleHighlighter = () => {
    const next = !isHighlighter;
    setIsHighlighter(next);
    setIsEraser(false);
    canvasRef.current?.eraseMode(false);
    if (next) { setStrokeColor("rgba(255,255,0,0.4)"); setStrokeWidth(20); }
    else { setStrokeColor("#000000"); setStrokeWidth(3); }
  };

  // Merges current strokes with background and returns the composite image.
  // Does NOT clear the canvas so the user can keep editing after navigating away and back.
  const capture = async () => {
    if (!canvasRef.current) return existingImage;
    try {
      const paths = await canvasRef.current.exportPaths();
      const hasNewStrokes = paths && paths.length > 0;
      if (existingImage && !hasNewStrokes) return existingImage;
      const strokesDataUrl = await canvasRef.current.exportImage("png");
      let result;
      if (existingImage && hasNewStrokes) {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const strokesImg = new Image();
        strokesImg.crossOrigin = "anonymous";
        await new Promise((res, rej) => { strokesImg.onload = res; strokesImg.onerror = rej; strokesImg.src = strokesDataUrl; });
        const bgImg = new Image();
        bgImg.crossOrigin = "anonymous";
        await new Promise((res, rej) => { bgImg.onload = res; bgImg.onerror = rej; bgImg.src = existingImage; });
        canvas.width = strokesImg.width || 800;
        canvas.height = strokesImg.height || 600;
        const bgAspect = bgImg.width / bgImg.height;
        const canvasAspect = canvas.width / canvas.height;
        let dw, dh, ox, oy;
        if (bgAspect > canvasAspect) { dw = canvas.width; dh = canvas.width / bgAspect; ox = 0; oy = (canvas.height - dh) / 2; }
        else { dh = canvas.height; dw = canvas.height * bgAspect; ox = (canvas.width - dw) / 2; oy = 0; }
        ctx.drawImage(bgImg, ox, oy, dw, dh);
        if (strokesImg.width > 0) ctx.drawImage(strokesImg, 0, 0);
        result = canvas.toDataURL("image/png");
      } else {
        result = strokesDataUrl;
      }
      onSave(result);
      return result;
    } catch (err) { console.error("Failed to capture canvas:", err); return existingImage; }
  };

  useImperativeHandle(ref, () => ({ capture }));

  return (
    <div>
      {/* Toolbar */}
      <div className="handwriting-modal-header d-flex align-items-center gap-2 flex-wrap p-2 mb-1" style={{ backgroundColor: "#CFE2FF" }}>
        <div className="d-flex align-items-center gap-1">
          {colorOptions.map((color) => (
            <button key={color} type="button"
              className={`color-btn ${strokeColor === color && !isEraser && !isHighlighter ? "active" : ""}`}
              style={{ backgroundColor: color }}
              onClick={() => { setStrokeColor(color); setIsEraser(false); setIsHighlighter(false); setStrokeWidth(3); canvasRef.current?.eraseMode(false); }}
            />
          ))}
        </div>
        <div className="d-flex align-items-center gap-1">
          {strokeOptions.map((w) => (
            <button key={w} type="button"
              className={`stroke-btn ${strokeWidth === w && !isHighlighter ? "active" : ""}`}
              onClick={() => { setStrokeWidth(w); setIsHighlighter(false); }}
            >
              <span style={{ width: w * 2, height: w * 2 }} className="stroke-preview" />
            </button>
          ))}
        </div>
        <div className="d-flex align-items-center gap-1">
          <Button variant={isHighlighter ? "warning" : "outline-primary"} size="sm" onClick={toggleHighlighter} title="Highlighter"><i className="fa-solid fa-highlighter" /></Button>
          <Button variant={isEraser ? "primary" : "outline-primary"} size="sm" onClick={toggleEraser} title="Eraser"><i className="fa-solid fa-eraser" /></Button>
          <Button variant="outline-primary" size="sm" onClick={() => canvasRef.current?.undo()} title="Undo"><i className="fa-solid fa-rotate-left" /></Button>
          <Button variant="outline-primary" size="sm" onClick={() => canvasRef.current?.redo()} title="Redo"><i className="fa-solid fa-rotate-right" /></Button>
          <Button variant="outline-primary" size="sm" onClick={() => canvasRef.current?.clearCanvas()} title="Clear"><i className="fa-solid fa-trash" /></Button>
        </div>
        <div className="ms-auto d-flex align-items-center gap-2">
          <Button variant="" className="rounded-0 text-primary" onClick={onExpand} title={isFullScreen ? "Collapse" : "Expand"}>
            <i className={`fa-solid ${isFullScreen ? "fa-arrows-minimize" : "fa-arrows-maximize"} fs-5`} />
          </Button>
        </div>
      </div>
      {/* Canvas */}
      <div className="handwriting-canvas-container" style={{ width: "100%", height: "1200px", overflow: "hidden" }}>
        <ReactSketchCanvas
          ref={canvasRef}
          strokeWidth={isHighlighter ? 20 : strokeWidth}
          strokeColor={isEraser ? "transparent" : (isHighlighter ? "rgba(255,255,0,0.4)" : strokeColor)}
          canvasColor="transparent"
          backgroundImage={existingImage || ""}
          preserveBackgroundImageAspectRatio="xMidYMid meet"
          style={{ width: "100%", height: "100%" }}
          allowOnlyPointerType="all"
          withTimestamp={true}
        />
      </div>
    </div>
  );
});

// Six-checkbox block for the PDF inclusion flags. Used in Download Options + Email modals.
// Template Title is rendered separately at the top with an editable text input
// so the user can override the title that appears on the PDF.
const PDF_OPTION_FIELDS = [
  ["includePatientName",   "Patient Name"],
  ["includeIdNumber",      "ID Number"],
  ["includeDob",           "Date of Birth"],
  ["includeMedicalAid",    "Medical Aid / IOD Info"],
  ["includeSessionDate",   "Session Date"],
];
const PdfOptionCheckboxes = ({ options, setOptions, templateTitle, setTemplateTitle }) => {
  const titleOn = options.includeTemplateTitle !== false;
  return (
    <>
      <div className="mb-3">
        <Form.Check
          type="checkbox"
          id="pdfopt-includeTemplateTitle"
          label="Template Title"
          checked={titleOn}
          onChange={(e) =>
            setOptions((prev) => ({ ...prev, includeTemplateTitle: e.target.checked }))
          }
        />
        {titleOn && (
          <Form.Control
            type="text"
            size="sm"
            className="mt-1"
            placeholder="Title shown on the PDF"
            value={templateTitle || ""}
            onChange={(e) => setTemplateTitle(e.target.value)}
            aria-label="PDF title"
          />
        )}
      </div>
      <Row className="g-2">
        {PDF_OPTION_FIELDS.map(([key, label]) => (
          <Col xs={6} key={key}>
            <Form.Check
              type="checkbox"
              id={`pdfopt-${key}`}
              label={label}
              checked={options[key] !== false}
              onChange={(e) => setOptions((prev) => ({ ...prev, [key]: e.target.checked }))}
            />
          </Col>
        ))}
      </Row>
    </>
  );
};

export const UpdateSession = ({
  sessionId,
  patientId,
  appointmentId,
  serviceName,
  appointmentStart,
  appointmentStatus,
  sections = {},
  notepadContentR = "",
  notepadContentType = "",
  handwritingImageR = null,
  templateId,
  practiceId,
  resourceId,
  activeSection,
  onSaved,
  mediaFilesR = [],
  inline = false,
  capturedValuesR = {},
  sessionsTemplateVersionR = null,
  updatedAtR = null,
  syncToProps,
}) => {
  const [notes, setNotes] = useState([]);
  const [notepadContent, setNotepadContent] = useState("");
  const [handwritingImage, setHandwritingImage] = useState(null);
  const [showHandwritingModal, setShowHandwritingModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isNarrow, setIsNarrow] = useState(window.innerWidth <= 1024);
  const [isLandscape, setIsLandscape] = useState(
    window.matchMedia("(orientation: landscape)").matches,
  );
  const [isSaving, setIsSaving] = useState(false);
  // Opt-in: mark the linked appointment Complete on save. Default driven by
  // the user's preferences.autoCompleteAppointments flag (off unless set).
  // Hidden for ad-hoc notes (no appointmentId) and already-completed appts.
  const [completeAppointment, setCompleteAppointment] = useState(
    !!getUserPreferences().autoCompleteAppointments,
  );
  const { toast, showToast, hideToast } = useToast();
  const { confirm, confirmProps } = useConfirm();
  const { addNotification } = useNotification() || {};
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    templateId || "",
  );
  const quillRef = useRef(null);
  const annotatorRefs = useRef([]);
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [hasLoadedExistingData, setHasLoadedExistingData] = useState(false);
  const [mediaImages, setMediaImages] = useState([]); // strings (dataURL or http)
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [camError, setCamError] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const loadedNotepadRef = useRef(false);
  const initialTemplateIdRef = useRef(templateId || "");
  const Navigate = useNavigate();
  // Pick the section to open based on what content the session actually has.
  // Priority follows the tab order in the nav: Template > Note Pad > Write > Media.
  // Template hit can come from either capturedValues (current schema) or the
  // legacy sections[] array (older sessions). Falls back to template if a
  // templateId is set, otherwise notepad.
  const resolveDefaultSection = (tid, hwImg, notepad, captured, sectionsArr, media, overrideSection) => {
    if (overrideSection) return overrideSection;
    const hasCaptured = captured && Object.keys(captured).length > 0;
    const hasSections = Array.isArray(sectionsArr) && sectionsArr.length > 0;
    const hasTemplateData = hasCaptured || hasSections;
    const hasNotepad = typeof notepad === "string" && notepad.trim().length > 0;
    const hasHandwriting = !!hwImg;
    const hasMedia = Array.isArray(media) && media.length > 0;
    if (hasTemplateData) return "apptTemplate";
    if (hasNotepad) return "apptNotepad";
    if (hasHandwriting) return "write";
    if (hasMedia) return "apptMedia";
    return tid ? "apptTemplate" : "apptNotepad";
  };
  const [localSection, setLocalSection] = useState(
    resolveDefaultSection(templateId, handwritingImageR, notepadContentR, capturedValuesR, sections, mediaFilesR, activeSection)
  );
  const [isFullScreen, setIsFullScreen] = useState(false);
  const inlineWriteRef = useRef(null);

  // NoteRenderer fill mode state
  const [capturedValues, setCapturedValues] = useState({});
  const [templatePages, setTemplatePages] = useState([]);
  const [templateVersion, setTemplateVersion] = useState(0);
  // Set when the session was filled against a template version that we
  // couldn't load from templateVersions (deleted, never published, etc.).
  // Shape: { storedVersion: number } — banner shown above the renderer.
  const [versionWarning, setVersionWarning] = useState(null);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [serverTimestamp, setServerTimestamp] = useState(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  // People (practitioners) in the logged-in user's practice(s), for the "To"
  // typeahead. Loaded when the email modal opens.
  const [people, setPeople] = useState([]);
  const [toSuggestOpen, setToSuggestOpen] = useState(false);
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const [pdfOptions, setPdfOptions] = useState({
    includeTemplateTitle: true,
    includePatientName: true,
    includeIdNumber: true,
    includeDob: true,
    includeMedicalAid: true,
    includeSessionDate: true,
  });
  // Editable PDF title. Defaults to the selected template's title; the user
  // can override it before downloading or emailing. Re-syncs whenever the
  // selected template changes so each new template loads with its own default.
  const [pdfTemplateTitle, setPdfTemplateTitle] = useState("");
  useEffect(() => {
    const tpl = templates.find((t) => t._id === selectedTemplateId);
    setPdfTemplateTitle(tpl?.templateTitle || "");
  }, [selectedTemplateId, templates]);
  const lastBackfilledSessionId = useRef(null);
  const [voiceTargetId, setVoiceTargetId] = useState(null);
  const [hideButtonText, setHideButtonText] = useState(false);

  useEffect(() => {
    const compute = () => {
      const portrait = window.matchMedia("(orientation: portrait)").matches;
      setHideButtonText(window.innerWidth <= 768 && portrait);
    };
    compute();
    const mql = window.matchMedia("(orientation: portrait)");
    const onMql = () => compute();
    window.addEventListener("resize", compute);
    if (mql.addEventListener) mql.addEventListener("change", onMql);
    else mql.addListener(onMql);
    return () => {
      window.removeEventListener("resize", compute);
      if (mql.removeEventListener) mql.removeEventListener("change", onMql);
      else mql.removeListener(onMql);
    };
  }, []);

  const handleSectionChange = async (key) => {
    // Capture write canvas while still visible before hiding it — exportImage fails on display:none
    if (localSection === "write" && key !== "write" && inlineWriteRef.current) {
      await inlineWriteRef.current.capture();
    }
    setLocalSection(key);
  };

  const handleExpand = async () => {
    // Capture canvas before fullscreen toggle — portaling remounts the tree, which resets the canvas
    if (inlineWriteRef.current) await inlineWriteRef.current.capture();
    setIsFullScreen((f) => !f);
  };

  // Sync localSection from activeSection prop
  useEffect(() => {
    if (activeSection) setLocalSection(activeSection);
  }, [activeSection]);

  const navItems = [
    { key: "apptTemplate", label: "Template", icon: "fa-light fa-file-waveform" },
    { key: "apptNotepad",  label: "Note Pad", icon: "fa-light fa-clipboard-medical" },
    { key: "write",        label: "Write",    icon: "fa-light fa-pen-nib" },
    { key: "apptMedia",    label: "Media",    icon: "fa-light fa-camera" },
  ];

  const safeHtml = (v) => (typeof v === "string" ? v : v ? String(v) : "");

  // shared normalizer
  const normalizeNotes = (incoming) => {
    const arr = Array.isArray(incoming?.sections)
      ? incoming.sections
      : Array.isArray(incoming)
        ? incoming
        : Array.isArray(incoming?.notes)
          ? incoming.notes
          : [];

    return arr.map((n) =>
      n.sessionsFieldType === "multiselect"
        ? {
            ...n,
            sessionsSectionNotes: {
              availableOptions: Array.isArray(
                n.sessionsSectionNotes?.availableOptions,
              )
                ? n.sessionsSectionNotes.availableOptions
                : Array.isArray(n.sessionsSectionNotes)
                  ? n.sessionsSectionNotes
                  : [],
              selectedOptions: Array.isArray(
                n.sessionsSectionNotes?.selectedOptions,
              )
                ? n.sessionsSectionNotes.selectedOptions
                : [],
            },
          }
        : {
            ...n,
            sessionsSectionNotes:
              typeof n.sessionsSectionNotes === "string"
                ? n.sessionsSectionNotes
                : (n.sessionsSectionNotes ?? "") + "",
          },
    );
  };

  // Build capturedValues from legacy sections[] by matching labels to page element labels.
  // Used when a session has no capturedValues (pre-v16) or when stored IDs no longer match
  // the template (e.g. due to unstable uid() generation before stable IDs were introduced).
  const buildCapturedValuesFromSections = (secs, pages) => {
    const sectionsArr = Array.isArray(secs) ? secs : [];
    const values = {};
    for (const page of (pages || [])) {
      for (const item of (page.items || [])) {
        const elements = item.kind === 'group' ? (item.children || []) : [item];
        for (const el of elements) {
          const sec = sectionsArr.find(s => s.sessionsSection === el.label);
          if (!sec) continue;
          if (sec.sessionsFieldType === 'multiselect') {
            values[el.id] = Array.isArray(sec.sessionsSectionNotes?.selectedOptions)
              ? sec.sessionsSectionNotes.selectedOptions : [];
          } else if (sec.sessionsFieldType === 'image') {
            values[el.id] = sec.sessionsImageURL || null;
          } else {
            values[el.id] = typeof sec.sessionsSectionNotes === 'string'
              ? sec.sessionsSectionNotes : '';
          }
        }
      }
    }
    return values;
  };

  // Loads templatePages + templateVersion for a session, honouring the
  // version it was filled against (sessionsTemplateVersionR). Falls back to
  // the current template pages when no version is stored or the version
  // snapshot can't be fetched, in which case versionWarning is set so the
  // user sees a banner.
  const applyTemplatePagesForSession = async (sel, storedVersion) => {
    const currentPages = sel
      ? (Array.isArray(sel.pages) && sel.pages.length > 0 ? sel.pages : notesToPages(sel.notes))
      : [];

    if (sel && typeof storedVersion === "number" && storedVersion > 0) {
      try {
        const ver = await fetchTemplateVersion(sel._id, storedVersion);
        if (ver && Array.isArray(ver.pages) && ver.pages.length > 0) {
          setTemplatePages(ver.pages);
          setTemplateVersion(storedVersion);
          setVersionWarning(null);
          return;
        }
        setVersionWarning({ storedVersion });
      } catch (err) {
        setVersionWarning({ storedVersion });
      }
    } else {
      setVersionWarning(null);
    }

    setTemplatePages(currentPages);
    setTemplateVersion(storedVersion || sel?.currentVersion || 0);
  };

  // media init/merge
  useEffect(() => {
    const urls = (mediaFilesR || [])
      .map((f) => (typeof f === "string" ? f : f?.fileUrl))
      .filter(Boolean);

    if (!urls.length) return;
    setMediaImages((prev) => {
      if (!prev.length) return urls;
      const seen = new Set(urls);
      return [...urls, ...prev.filter((u) => !seen.has(u))];
    });
  }, [sessionId, mediaFilesR]);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startStream = async () => {
    try {
      stopStream();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCamError(null);
    } catch (e) {
      setCamError(e.message || "Camera access failed");
    }
  };

  useEffect(() => {
    if (localSection === "apptMedia") startStream();
    else stopStream();
    return stopStream;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSection, facingMode]);

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    setMediaImages((prev) => [dataUrl, ...prev]);
  };

  const removePhoto = (idx) =>
    setMediaImages((prev) => prev.filter((_, i) => i !== idx));

  const flipCamera = () =>
    setFacingMode((m) => (m === "environment" ? "user" : "environment"));

  // Recorder wiring
  const vr = useVoiceRecorder();
  const [recOpen, setRecOpen] = useState(false);
  const [recTarget, setRecTarget] = useState(null); // { type:'template'|'notepad', index? }

  const openRecorderForTemplate = (index) => {
    setRecTarget({ type: "template", index });
    setRecOpen(true);
  };
  const openRecorderForNotepad = () => {
    setRecTarget({ type: "notepad" });
    setRecOpen(true);
  };
  const closeRecorder = () => {
    if (vr.listening) vr.stop();
    setRecOpen(false);
    setRecTarget(null);
    setVoiceTargetId(null); // clears NoteRenderer field target too
  };

  const insertToQuill = (value) => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    quill.focus();
    const end = quill.getLength();
    const needsSpace = end > 1 && /\S/.test(quill.getText(end - 2, 1));
    const text = (needsSpace ? " " : "") + (value || "");
    quill.insertText(end, text, "user");
    quill.setSelection(end + text.length, 0, "user");
    setTimeout(() => setNotepadContent(quill.root.innerHTML), 0);
  };

  const applyTranscriptToTarget = (text) => {
    const cleaned = (text || "").trim();
    if (!cleaned) return;
    if (voiceTargetId) {
      // NoteRenderer field voice path — applies transcript to capturedValues by element ID
      setCapturedValues(prev => ({
        ...prev,
        [voiceTargetId]: prev[voiceTargetId]
          ? prev[voiceTargetId] + ' ' + cleaned
          : cleaned,
      }));
      setVoiceTargetId(null);
    } else if (recTarget?.type === "notepad") {
      // Notepad voice path — unchanged from existing implementation
      insertToQuill(cleaned);
    }
    // If neither branch matches (recTarget is template type from old path), do nothing —
    // template voice is now handled exclusively through voiceTargetId.
  };

  useEffect(
    () => () => {
      if (vr.listening) vr.stop();
    },
    [vr.listening],
  );

  useEffect(() => {
    annotatorRefs.current = annotatorRefs.current.slice(0, notes.length);
  }, [notes.length]);

  const commitAllImages = async () => {
    const copy = [...notes];
    let changed = false;
    for (let idx = 0; idx < copy.length; idx++) {
      const note = copy[idx];
      if (note?.sessionsFieldType !== "image") continue;
      const ref = annotatorRefs.current[idx];
      if (!ref || typeof ref.mergeImage !== "function") continue;
      try {
        const hasStrokes = typeof ref.hasStrokes === "function" && await ref.hasStrokes();
        if (!hasStrokes) continue;
        const merged = await ref.mergeImage();
        if (merged && typeof merged === "string") {
          copy[idx] = { ...note, sessionsImageURL: merged };
          changed = true;
        }
      } catch (e) {
        console.warn("mergeImage failed for index", idx, e);
      }
    }
    if (changed) setNotes(copy);
    return copy;
  };

  // (template section is always mounted so no need to commit on section change)

  // Templates fetch
  useEffect(() => {
    const initializeData = async () => {
      const token = sessionStorage.getItem("user");
      if (!token) return;
      try {
        const decoded = jwtDecode(token);
        const userPractices = decoded.practice;
        if (!userPractices || userPractices.length === 0) return;

        const templatesData = await fetchPracticeTemplates(decoded._id);
        const filteredTemplates = templatesData.filter((template) =>
          template.templatePracticeId.some((p) => userPractices.includes(p)),
        );

        // Dropdown source: hide inactive templates and sort alphabetically.
        // `filteredTemplates` is kept intact so already-selected (possibly inactive)
        // templates still resolve on session reopen.
        const selectableTemplates = filteredTemplates
          .filter((t) => t.templateStatus !== "Inactive")
          .sort((a, b) =>
            (a.templateTitle || "").localeCompare(b.templateTitle || "", undefined, { sensitivity: "base" })
          );

        const initialTemplates = selectableTemplates.filter(
          (t) => t.templateInitial === "initial",
        );
        const followUpTemplates = selectableTemplates.filter(
          (t) => t.templateInitial === "followup",
        );

        // If the session is already attached to an inactive template, surface it
        // in the dropdown as a disabled option so the dropdown shows the
        // template name (instead of falling back to an unrelated option) while
        // still preventing reselection.
        const currentTplForDropdown = templateId
          ? filteredTemplates.find((t) => t._id === templateId)
          : null;
        if (
          currentTplForDropdown &&
          currentTplForDropdown.templateStatus === "Inactive"
        ) {
          const tagged = { ...currentTplForDropdown, _inactiveSelected: true };
          if (currentTplForDropdown.templateInitial === "followup") {
            followUpTemplates.unshift(tagged);
          } else {
            initialTemplates.unshift(tagged);
          }
        }

        setDropdownOptions([
          { label: "Initial Assessment", options: initialTemplates },
          { label: "Follow Up", options: followUpTemplates },
        ]);
        setTemplates(filteredTemplates);

        // If a template is already selected (session reopen), load its pages.
        // Prefer the version the session was filled against, falling back to
        // current pages with a banner if the version snapshot is unavailable.
        const currentTemplateId = templateId || "";
        if (currentTemplateId) {
          const sel = filteredTemplates.find((t) => t._id === currentTemplateId);
          if (sel) {
            await applyTemplatePagesForSession(sel, sessionsTemplateVersionR);
          }
        }
      } catch (error) {
        console.error("Error fetching templates:", error);
      }
    };
    initializeData();
  }, []);

  // ✅ Rehydrate all session state when sessionId changes.
  // Props (sections, templateId, etc.) are intentionally read from closure — they reflect
  // the current render's values and must NOT be in the dep array to avoid re-render loops.
  useEffect(() => {
    const normalized = normalizeNotes(sections);
    setNotes(normalized);
    setSelectedTemplateId(templateId || "");
    initialTemplateIdRef.current = templateId || "";
    annotatorRefs.current = Array(normalized.length).fill(null);
    setCapturedValues(capturedValuesR || {});
    setServerTimestamp(updatedAtR || null);

    // Reset templatePages for this session's template, honouring the version
    // it was filled against. templates[] is already populated on navigation
    // (fetched on initial mount). On first mount templates is empty — the []
    // fetch effect handles that path.
    if (templates.length > 0) {
      if (templateId) {
        const sel = templates.find((t) => t._id === templateId);
        applyTemplatePagesForSession(sel, sessionsTemplateVersionR);
      } else {
        setTemplatePages([]);
        setTemplateVersion(0);
        setVersionWarning(null);
      }
    }

    // Reset to the tab where this session's content actually lives
    setLocalSection(resolveDefaultSection(templateId, handwritingImageR, notepadContentR, capturedValuesR, sections, mediaFilesR, activeSection));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Backfill capturedValues from sections[] for old sessions (pre-v16) or when stored element IDs
  // no longer match the current template pages (e.g. IDs changed after template edit).
  // Runs whenever templatePages or sessionId changes. Guards ensure it only runs once per session
  // and only when capturedValuesR doesn't already have matching keys.
  useEffect(() => {
    if (!templatePages.length) return;
    const sectionsArr = Array.isArray(sections) ? sections : [];
    if (!sectionsArr.length) return;
    if (lastBackfilledSessionId.current === sessionId) return; // already backfilled this session

    // Collect all page element IDs to check if capturedValuesR keys match
    const pageIds = new Set();
    const collectIds = (items) => {
      for (const item of (items || [])) {
        if (item.kind === 'group') collectIds(item.children);
        else pageIds.add(item.id);
      }
    };
    templatePages.forEach(p => collectIds(p.items));

    const capturedMatchesTemplate = capturedValuesR &&
      Object.keys(capturedValuesR).length > 0 &&
      Object.keys(capturedValuesR).some(k => pageIds.has(k));

    if (capturedMatchesTemplate) return; // capturedValuesR is good — already loaded

    const derived = buildCapturedValuesFromSections(sectionsArr, templatePages);
    if (Object.keys(derived).length > 0) {
      lastBackfilledSessionId.current = sessionId;
      setCapturedValues(derived);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templatePages, sessionId]);

  // Notepad content: once per sessionId
  useEffect(() => {
    loadedNotepadRef.current = false;
  }, [sessionId]);

  useEffect(() => {
    if (loadedNotepadRef.current) return;
    const content =
      safeHtml(notepadContentR) ??
      safeHtml(sections?.notepadContent) ??
      safeHtml(sections?.sessionsNotepadContent) ??
      "";
    setNotepadContent(content);
    loadedNotepadRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Handwriting image: sync with prop when sessionId or prop changes
  useEffect(() => {
    setHandwritingImage(handwritingImageR || null);
  }, [sessionId, handwritingImageR]);

  // Detect mobile device for handwriting button visibility
  useEffect(() => {
    const checkMobile = () => {
      const mobile =
        window.innerWidth <= 1024 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        );
      setIsMobile(mobile);
      setIsNarrow(window.innerWidth <= 1024);
      setIsLandscape(window.matchMedia("(orientation: landscape)").matches);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Template select change
  const [activeNotePage, setActiveNotePage] = useState(0);

  const handleTemplateChange = async (newId) => {
    // UpdateSession is always editing a saved session — changing the template
    // will overwrite it. Gate the change on an explicit confirm.
    if (newId !== selectedTemplateId) {
      const ok = await confirm({
        title: "Overwrite saved session?",
        message:
          "Changing the template will overwrite this saved session.\n\n" +
          "If you want to record a new session, cancel and use \"Add Note\" instead.",
        confirmLabel: "Overwrite",
        cancelLabel: "Cancel",
        confirmVariant: "danger",
      });
      if (!ok) return;
    }
    commitAllImages();
    setSelectedTemplateId(newId);
    setActiveNotePage(0);
    const sel = templates.find((t) => t._id === newId);
    const pages = sel
      ? (Array.isArray(sel.pages) && sel.pages.length > 0 ? sel.pages : notesToPages(sel.notes))
      : [];
    setTemplatePages(pages);
    setTemplateVersion(sel?.currentVersion || 0);
    setVersionWarning(null);
    // Build initial values from defaultValue on each element
    const init = {};
    for (const page of pages) {
      for (const item of (page.items || [])) {
        if (item.kind === 'group') {
          for (const child of (item.children || [])) {
            if (child.defaultValue !== undefined) init[child.id] = child.defaultValue;
          }
        } else if (item.defaultValue !== undefined) {
          init[item.id] = item.defaultValue;
        }
      }
    }
    setCapturedValues(init);
    setNotes([]); // clear legacy notes state to avoid stale data
    annotatorRefs.current = [];
  };

  // Helpers used in render
  const getFieldValue = (note) => {
    if (!note?.sessionsSectionNotes) return "";
    if (typeof note.sessionsSectionNotes === "object") return "";
    return note.sessionsSectionNotes;
  };
  const handleTemplateFieldChange = (index, value) => {
    setNotes((prev) => {
      const updated = [...prev];
      updated[index].sessionsSectionNotes = value;
      return updated;
    });
  };
  const handleMultiselectChange = (index, option) => {
    setNotes((prev) =>
      prev.map((note, i) => {
        if (i !== index) return note;
        const current = note.sessionsSectionNotes?.selectedOptions || [];
        const selected = current.includes(option)
          ? current.filter((x) => x !== option)
          : [...current, option];
        return {
          ...note,
          sessionsSectionNotes: {
            ...note.sessionsSectionNotes,
            selectedOptions: selected,
          },
        };
      }),
    );
  };

  // Core save logic — returns updated processedNotes; callers handle toast/navigation.
  const performSave = async () => {
    let currentHandwriting = handwritingImage;
    if (localSection === "write" && inlineWriteRef.current) {
      const captured = await inlineWriteRef.current.capture();
      if (captured) { currentHandwriting = captured; setHandwritingImage(captured); }
    }

    const mergedNotes = await commitAllImages();

    const processedNotes = [];
    for (let i = 0; i < (mergedNotes || []).length; i++) {
      const n = mergedNotes[i];
      if (n.sessionsFieldType !== "image") {
        processedNotes.push(n);
        continue;
      }
      const url = typeof n.sessionsImageURL === "string" ? n.sessionsImageURL : "";
      if (url && url.startsWith("data:")) {
        const blob = await fetch(url).then((r) => r.blob());
        const fd = new FormData();
        fd.append("image", blob);
        fd.append("x-image-caption", "Session Image Upload");
        fd.append("patientId", patientId);
        fd.append("appointmentId", appointmentId);
        fd.append("type", "session");
        const up = await uploadImage(fd);
        processedNotes.push({ ...n, sessionsImageURL: up.imageUrl });
      } else {
        processedNotes.push(n);
      }
    }

    const finalMediaFiles = [];
    for (let idx = 0; idx < (mediaImages || []).length; idx++) {
      const m = mediaImages[idx];
      if (typeof m !== "string") continue;
      if (m.startsWith("data:")) {
        const blob = await fetch(m).then((r) => r.blob());
        const fd = new FormData();
        fd.append("image", blob);
        fd.append("x-image-caption", `Media Photo ${idx + 1}`);
        fd.append("patientId", patientId);
        fd.append("appointmentId", appointmentId);
        fd.append("type", "session");
        const up = await uploadImage(fd);
        finalMediaFiles.push({
          fileName: `media-photo-${idx + 1}`,
          fileUrl: up.imageUrl,
          fileType: "image",
          uploadedAt: new Date().toISOString(),
          description: `Captured photo ${idx + 1}`,
        });
      } else if (/^https?:\/\//.test(m)) {
        finalMediaFiles.push({
          fileName: `media-photo-${idx + 1}`,
          fileUrl: m,
          fileType: "image",
          uploadedAt: new Date().toISOString(),
          description: `Captured photo ${idx + 1}`,
        });
      }
    }
    const seen = new Set();
    const mediaFiles = finalMediaFiles.filter((f) => {
      const u = f.fileUrl;
      if (!u || seen.has(u)) return false;
      seen.add(u);
      return true;
    });

    const includeTemplateChange =
      selectedTemplateId && selectedTemplateId !== initialTemplateIdRef.current;

    const payload = {
      sessionsPatientId: patientId,
      sessionsPracticeId: practiceId,
      sessionsAppointmentId: appointmentId,
      sessionsServiceName: serviceName,
      sessionsResourceId: resourceId,
      sessionsAppointmentStart: appointmentStart,
      ...(includeTemplateChange ? { sessionsTemplateId: selectedTemplateId } : {}),
      sections: processedNotes,
      ...(notepadContent?.trim() ? { notepadContent, notepadContentType: "html" } : {}),
      ...(currentHandwriting ? { handwritingImage: currentHandwriting } : {}),
      ...(mediaFiles.length ? { mediaFiles } : {}),
      capturedValues,
      sessionsTemplateVersion: templateVersion,
      clientTimestamp: serverTimestamp,
      templatePages,
    };

    let resp;
    try {
      resp = await updateSessionById(sessionId, payload);
    } catch (err) {
      // 409 = stale write: DB was updated after our last known timestamp.
      // Retry once with the server's current timestamp so we don't block the user.
      const serverTs = err?.response?.data?.serverUpdatedAt;
      if (err?.response?.status === 409 && serverTs) {
        payload.clientTimestamp = serverTs;
        resp = await updateSessionById(sessionId, payload);
      } else {
        throw err;
      }
    }
    setServerTimestamp(resp?.updatedAt || null);
    initialTemplateIdRef.current = selectedTemplateId;
    setNotes(processedNotes);
    return processedNotes;
  };

  // Save button — saves and navigates as before
  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      await performSave();
      // Opt-in appointment completion. Only when ticked, this is linked to
      // an appointment, and that appointment isn't already Completed.
      // "New Patient" → "Completed" is blocked server-side; skip the call so
      // we don't surface an avoidable 409 to the user.
      if (
        completeAppointment &&
        appointmentId &&
        appointmentStatus !== "Completed" &&
        appointmentStatus !== "New Patient"
      ) {
        try {
          await updateAppointmentStatusByPatientId(appointmentId, "Completed");
        } catch (statusErr) {
          console.error("Failed to mark appointment complete:", statusErr);
        }
      }
      stopStream();
      showToast("success", "Session saved successfully!");
      const isUnregistered = String(patientId) === "1";
      setTimeout(() => {
        if (inline) {
          onSaved?.();
        } else if (isUnregistered) {
          Navigate(getBookingsReturnPath());
        } else {
          // Always drop the user on the patient's "previous notes" view
          // (URL ends in /1) so they immediately see what they just saved
          // alongside the rest of the history.
          onSaved?.();
          Navigate(`/patients/appointment/${patientId}/1`);
        }
      }, 1500);
    } catch (e) {
      console.error("UpdateSession save failed", e);
    } finally {
      setIsSaving(false);
    }
  };

  // Download — open the Download Options modal; actual download happens after confirm
  const handleSaveAndDownload = () => {
    if (!sessionId || !selectedTemplateId || !templatePages.length) return;
    setShowDownloadOptions(true);
  };

  // Confirm Download — save first, then download PDF with chosen options.
  // After download, navigate to the patient's "previous notes" view so the
  // newly saved session lands in the history list immediately.
  const handleConfirmDownload = async () => {
    if (!sessionId || !selectedTemplateId || !templatePages.length) return;
    setShowDownloadOptions(false);
    setPdfExporting(true);
    try {
      await performSave();
      await exportSessionTemplatePdf(sessionId, { ...pdfOptions, templateTitle: pdfTemplateTitle });
      if (!inline && String(patientId) !== "1") {
        Navigate(`/patients/appointment/${patientId}/1`);
      }
    } catch (err) {
      console.error("Save and download failed:", err);
      showToast("error", "Failed to save or download");
    } finally {
      setPdfExporting(false);
    }
  };

  // Email — save first, then show email form (with report template prefilled)
  const handleEmailClick = async () => {
    if (!sessionId || !selectedTemplateId || !templatePages.length) return;
    setIsSaving(true);
    try {
      await performSave();
      const { subject, message, ccSender, ccAddress } = await buildReportEmailPrefill();
      setEmailSubject(subject || "Session Notes");
      setEmailMessage(message || "");
      setEmailTo("");
      // Combine the sender CC (if enabled) with any configured CC addresses
      // from the report template. The user can still edit the field below.
      const ccParts = [];
      if (ccSender) {
        try {
          const token = sessionStorage.getItem("user");
          const senderEmail = token ? (jwtDecode(token)?.email || "") : "";
          if (senderEmail) ccParts.push(senderEmail);
        } catch (e) { /* ignore decode errors */ }
      }
      if (ccAddress) {
        ccAddress
          .split(/[,;]/)
          .map((s) => s.trim())
          .filter(Boolean)
          .forEach((addr) => {
            if (!ccParts.some((p) => p.toLowerCase() === addr.toLowerCase())) {
              ccParts.push(addr);
            }
          });
      }
      setEmailCc(ccParts.join("; "));
      setToSuggestOpen(false);
      // Load people for the "To" typeahead — scoped server-side to the
      // logged-in user's practice(s). getPeopleForUser throws on 404 (none).
      try {
        const token = sessionStorage.getItem("user");
        const list = token ? await getPeopleForUser(token) : [];
        setPeople(Array.isArray(list) ? list : []);
      } catch (e) {
        setPeople([]);
      }
      setShowEmailForm(true);
    } catch (err) {
      console.error("Save before email failed:", err);
      showToast("error", "Failed to save session");
    } finally {
      setIsSaving(false);
    }
  };

  // Resolve report email template variables for this session.
  // Returns { subject, message } as plain text (HTML stripped) or empty strings if no template.
  const buildReportEmailPrefill = async () => {
    try {
      const [finCommsList, patient, practices] = await Promise.all([
        practiceId ? getFinComms(practiceId) : Promise.resolve([]),
        patientId ? getPatientById(patientId) : Promise.resolve(null),
        practiceId ? getPracticesByNumbers([practiceId]) : Promise.resolve([]),
      ]);

      const fin = Array.isArray(finCommsList) ? finCommsList[0] : null;
      const subjectTpl = fin?.report?.subject ?? fin?.reportSubject ?? "";
      const bodyTplHtml = fin?.report?.text ?? fin?.reportText ?? "";
      const ccSender = !!(fin?.report?.ccSender ?? fin?.reportCcSender);
      const ccAddress = String(fin?.report?.ccAddress ?? fin?.reportCcAddress ?? "").trim();
      if (!subjectTpl && !bodyTplHtml) return { subject: "", message: "", ccSender, ccAddress };

      const practice = Array.isArray(practices) ? practices[0] : practices;
      const tpl = templates.find((t) => t._id === selectedTemplateId);
      const sessionDateStr = (appointmentStart || "").split("T")[0] || "";
      const patientFull = [patient?.patientFirstName, patient?.patientLastName]
        .filter(Boolean).join(" ").trim();

      const ctx = {
        patientName: patientFull,
        sessionDate: sessionDateStr,
        templateName: tpl?.templateTitle || "",
        practiceName: practice?.practiceName || "",
        practicePhone: practice?.practicePhoneNumber || "",
        practiceEmail: practice?.practiceEmail || "",
      };

      const fill = (tplStr) =>
        String(tplStr || "").replace(/\$\{\s*([^}]+?)\s*\}/g, (_, key) => {
          const v = ctx[key.trim()];
          return v === undefined || v === null ? "" : String(v);
        });

      const htmlToText = (html) =>
        String(html || "")
          .replace(/<\s*br\s*\/?>/gi, "\n")
          .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
          .replace(/<li[^>]*>/gi, "• ")
          .replace(/<[^>]+>/g, "")
          .replace(/&nbsp;/g, " ")
          .replace(/&amp;/g, "&")
          .replace(/&lt;/g, "<")
          .replace(/&gt;/g, ">")
          .replace(/\n{3,}/g, "\n\n")
          .trim();

      return {
        subject: fill(subjectTpl),
        message: htmlToText(fill(bodyTplHtml)),
        ccSender,
        ccAddress,
      };
    } catch (err) {
      console.error("Failed to build report email prefill:", err);
      return { subject: "", message: "", ccSender: false, ccAddress: "" };
    }
  };

  // --- "To" field typeahead -------------------------------------------------
  // The active query is the address segment currently being typed: everything
  // after the last ";" so earlier committed recipients are left untouched.
  const toQuery = (() => {
    const idx = emailTo.lastIndexOf(";");
    return (idx >= 0 ? emailTo.slice(idx + 1) : emailTo).trim().toLowerCase();
  })();

  const toSuggestions =
    toSuggestOpen && toQuery.length >= 1
      ? people
          .filter((p) => {
            const emails = personEmails(p);
            if (!emails.length) return false; // nothing to fill — skip
            const first = (p.firstName || "").toLowerCase();
            const last = (p.lastName || "").toLowerCase();
            const primary = emails[0].toLowerCase();
            return (
              first.includes(toQuery) ||
              last.includes(toQuery) ||
              primary.includes(toQuery)
            );
          })
          .slice(0, 8)
      : [];

  // Pick a person: their primary email goes to "To", any extra emails go to CC.
  const handlePickPerson = (p) => {
    const emails = personEmails(p);
    if (!emails.length) return;
    const [primary, ...extras] = emails;

    // Replace the partial segment being typed with the chosen primary email,
    // preserving any recipients already committed before the last ";".
    const idx = emailTo.lastIndexOf(";");
    const head = idx >= 0 ? emailTo.slice(0, idx) : "";
    const toAddrs = head.split(";").map((s) => s.trim()).filter(Boolean);
    if (!toAddrs.includes(primary)) toAddrs.push(primary);
    setEmailTo(toAddrs.join("; "));

    // Append extra emails to CC (deduped, and not already a "To" recipient).
    if (extras.length) {
      const ccAddrs = emailCc.split(";").map((s) => s.trim()).filter(Boolean);
      extras.forEach((e) => {
        if (!ccAddrs.includes(e) && !toAddrs.includes(e)) ccAddrs.push(e);
      });
      setEmailCc(ccAddrs.join("; "));
    }

    setToSuggestOpen(false);
  };

  // Send email — close modal immediately, send in background, show toast + nav notification on result
  const handleSendEmail = () => {
    if (!emailTo.trim()) {
      showToast("error", "Please enter a recipient email address");
      return;
    }
    const to = emailTo.trim();
    const payload = {
      to,
      cc: emailCc.trim(),
      subject: emailSubject.trim() || "Session Notes",
      message: emailMessage.trim(),
      options: { ...pdfOptions, templateTitle: pdfTemplateTitle },
    };
    setShowEmailForm(false);
    emailSessionTemplatePdf(sessionId, payload)
      .then(() => {
        showToast("success", "Email sent successfully!");
        if (typeof addNotification === "function") {
          addNotification({
            message: `Session notes PDF emailed to ${to}`,
            timestamp: Date.now(),
            type: "email",
          });
        }
      })
      .catch((err) => {
        console.error("Email send failed:", err);
        showToast("error", "Failed to send email");
      });
    // Navigate to the patient's previous-notes view so the saved session
    // lands in the history list right away (email send continues in the
    // background and toasts when done).
    if (!inline && String(patientId) !== "1") {
      Navigate(`/patients/appointment/${patientId}/1`);
    }
  };

  const renderCurrentSection = () => {
    switch (localSection) {
      case "apptNotepad":
        return (
          <div className="mt-1">
            <Row className="align-items-center mb-1">
              <Col md={12}>
                <div className="d-flex align-items-center">
                  <div>
                    <p className="text-muted mb-0">
                      Use this space for quick notes during the session.
                    </p>
                  </div>
                  <div className="d-flex align-items-center ms-auto gap-1">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="p-1 border-0"
                      aria-label="Speak to add to notepad"
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); openRecorderForNotepad(); }}
                      title="Speak to add to notepad"
                    >
                      <i className="fa-light fa-microphone fs-6" />
                    </Button>
                    <Button variant="" className="rounded-0 text-primary" onClick={handleExpand} title={isFullScreen ? "Collapse" : "Expand"}>
                      <i className={`fa-light ${isFullScreen ? "fa-arrows-minimize" : "fa-arrows-maximize"} fs-5`} />
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <div className="bg-white rounded shadow-sm notepad-container">
                  <ReactQuill
                    key={`quill-${sessionId}`}
                    ref={quillRef}
                    theme="snow"
                    readOnly={false}
                    value={notepadContent}
                    onChange={(html) => setNotepadContent(html)}
                    modules={{
                      toolbar: [
                        ["bold", "italic", "underline"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        ["link"],
                      ],
                    }}
                    formats={["bold", "italic", "underline", "list", "bullet", "link"]}
                    style={{ minHeight: "60vh", height: "60vh" }}
                    placeholder="Start typing your notes here..."
                  />
                </div>
              </Col>
            </Row>
          </div>
        );

      case "apptMedia":
        return (
          <div className="mt-1">
            <Row className="align-items-center mb-3">
              <Col>
              </Col>
              <Col xs="auto" className="d-flex align-items-center gap-1">
                <Button variant="" className="rounded-0 text-primary" onClick={handleExpand} title={isFullScreen ? "Collapse" : "Expand"}>
                  <i className={`fa-light ${isFullScreen ? "fa-arrows-minimize" : "fa-arrows-maximize"} fs-5`} />
                </Button>
              </Col>
            </Row>

            {camError ? (
              <Alert variant="warning" className="mb-3">
                Camera access failed ({camError}). You can still upload from gallery:
                <Form.Control
                  className="mt-2"
                  type="file"
                  accept="image/*"
                  multiple
                  capture="environment"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    files.forEach((file) => {
                      const reader = new FileReader();
                      reader.onload = () => setMediaImages((prev) => [reader.result, ...prev]);
                      reader.readAsDataURL(file);
                    });
                    e.target.value = "";
                  }}
                />
              </Alert>
            ) : (
              <div className="rounded right-scroll shadow-sm mb-3" style={{ position: "relative" }}>
                <video ref={videoRef} playsInline muted style={{ width: "100%", height: "auto", background: "#000" }} />
                <div className="position-absolute bottom-0 start-0 end-0 d-flex justify-content-center gap-3 p-3">
                  <Button variant="light" className="rounded-circle" onClick={capturePhoto} aria-label="Capture">
                    <i className="bi bi-camera-fill" />
                  </Button>
                  <Button variant="light" className="rounded-circle" onClick={flipCamera} aria-label="Flip">
                    <i className="bi bi-arrow-repeat" />
                  </Button>
                </div>
              </div>
            )}

            {mediaImages.length > 0 && (
              <>
                <h6 className="text-primary">Photos ({mediaImages.length})</h6>
                <Row>
                  {mediaImages.map((url, idx) => (
                    <Col xs={4} sm={3} md={2} key={idx} className="mb-3">
                      <div className="position-relative border rounded right-scroll">
                        <img
                          src={url}
                          alt={`photo ${idx + 1}`}
                          style={{ width: "100%", height: 100, objectFit: "cover", cursor: "pointer" }}
                          onClick={() => setLightboxUrl(url)}
                          title="Click to enlarge"
                        />
                        <Button variant="danger" size="sm" className="position-absolute top-0 end-0 m-1 p-1" onClick={() => removePhoto(idx)} aria-label="Remove" title="Remove">
                          ×
                        </Button>
                      </div>
                    </Col>
                  ))}
                </Row>
              </>
            )}

            <Modal show={!!lightboxUrl} onHide={() => setLightboxUrl(null)} centered size="lg">
              <Modal.Header closeButton />
              <Modal.Body className="p-0 text-center bg-dark">
                {lightboxUrl && (
                  <img
                    src={lightboxUrl}
                    alt="Full size"
                    style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }}
                  />
                )}
              </Modal.Body>
            </Modal>
          </div>
        );

      default:
        return null; // apptTemplate rendered separately (always mounted)
    }
  };

  const outerStyle = isFullScreen
    ? { position: "fixed", inset: 0, zIndex: 1049, background: "white", overflowY: "auto", display: "flex", flexDirection: "column" }
    : { marginTop: "4px" };

  const shortLabels = isLandscape || isNarrow;

  const sessionContent = (
    <div className="border" style={outerStyle}>
      <AppToast show={toast.show} onClose={hideToast} variant={toast.variant} message={toast.message} />
      <ConfirmModal {...confirmProps} />

      <div className={`d-flex flex-grow-1 ${isMobile ? "flex-column" : ""}`}>
        {/* Nav: top row on mobile, left column on desktop */}
        <div
          className={`d-flex gap-1 p-2 ${isMobile ? "flex-row flex-wrap border-bottom" : "flex-column border-end"}`}
          style={isMobile ? {} : { width: "130px", flexShrink: 0 }}
        >
          {syncToProps && (
            <>
              <SyncToButton {...syncToProps} shortLabel={isMobile} />
              {!isMobile && <hr className="my-2 mx-0" />}
            </>
          )}
          {navItems.map(({ key, label, icon }) => (
            <Button
              key={key}
              variant=""
              className="rounded-0 d-flex align-items-center gap-2"
              style={{
                backgroundColor: localSection === key ? "#cfe3ff" : "white",
                borderColor: localSection === key ? "#6eaaee" : "#dee2e6",
                color: "#1a3a6b",
                fontWeight: localSection === key ? "600" : "normal",
              }}
              onClick={() => handleSectionChange(key)}
            >
              <i className={icon} />
              {!isMobile && label}
            </Button>
          ))}
          {!isMobile && <hr className="my-2 mx-0" />}
          {appointmentId &&
            appointmentStatus !== "Completed" &&
            appointmentStatus !== "New Patient" && (
              <Form.Check
                type="checkbox"
                id="editsession-complete-appointment"
                className="mb-1"
                label="Complete appointment"
                checked={completeAppointment}
                onChange={(e) => setCompleteAppointment(e.target.checked)}
              />
            )}
          <Button
            variant="primary"
            className="rounded-0 d-flex align-items-center gap-2"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            <i className="fa-regular fa-floppy-disk" />
            {!isMobile && (isSaving ? "Saving..." : "Save")}
          </Button>
        </div>

        {/* Section content */}
        <div className="p-2 flex-grow-1">
          {/* Write section always mounted so canvas state persists across section switches */}
          <div style={{ display: localSection === "write" ? "block" : "none" }}>
            <InlineWriteSection
              ref={inlineWriteRef}
              onSave={setHandwritingImage}
              existingImage={handwritingImage}
              onExpand={handleExpand}
              isFullScreen={isFullScreen}
            />
          </div>
          {/* Template section always mounted so ImageAnnotator canvas state persists */}
          <div style={{ display: localSection === "apptTemplate" ? "block" : "none" }}>
            <Row className="mb-3 mt-1">
              <Col xs={12} className="d-flex align-items-center gap-2 flex-nowrap" style={{ minWidth: 0 }}>
                <Form.Label className="mb-0 text-primary fw-bold flex-shrink-0">Template:</Form.Label>
                <Form.Select
                  controlid="formGridSessionTemplate"
                  className="flex-shrink-1"
                  style={{ minWidth: 0, flex: "1 1 auto" }}
                  value={selectedTemplateId}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                >
                  <option value="" disabled>Choose a template...</option>
                  {dropdownOptions.map((category, idx) => (
                    <React.Fragment key={idx}>
                      <option disabled className="text-primary fw-bold">{category.label}</option>
                      {category.options.map((template) => (
                        <option
                          key={template._id}
                          value={template._id}
                          disabled={!!template._inactiveSelected}
                        >
                          {template.templateTitle}
                          {template._inactiveSelected ? " (Inactive)" : ""}
                        </option>
                      ))}
                    </React.Fragment>
                  ))}
                </Form.Select>
                <ButtonGroup className="gap-1 flex-shrink-0">
                  {sessionId && selectedTemplateId && templatePages.length > 0 && (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="rounded-0 pdf-action-btn"
                        title="Save and download session notes as PDF"
                        disabled={pdfExporting || isSaving}
                        onClick={handleSaveAndDownload}
                      >
                        {pdfExporting
                          ? <><i className="fa-light fa-spinner fa-spin me-md-1" />{!hideButtonText && <span>Downloading...</span>}</>
                          : <><i className="fa fa-download me-md-1" />{!hideButtonText && <span>Download</span>}</>
                        }
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="rounded-0 pdf-action-btn"
                        title="Save and email session notes as PDF"
                        disabled={isSaving}
                        onClick={handleEmailClick}
                      >
                        {isSaving
                          ? <><i className="fa-light fa-spinner fa-spin me-md-1" />{!hideButtonText && <span>Saving...</span>}</>
                          : <><i className="fa-regular fa-envelope me-md-1" />{!hideButtonText && <span>Email</span>}</>
                        }
                      </Button>
                    </>
                  )}
                  <Button variant="secondary" size="sm" className="rounded-0" onClick={handleExpand} title={isFullScreen ? "Collapse" : "Expand"}>
                    <i className={`fa-light ${isFullScreen ? "fa-arrows-minimize" : "fa-arrows-maximize"}`} />
                  </Button>
                </ButtonGroup>
              </Col>
            </Row>

            {selectedTemplateId && templatePages.length > 0 ? (
              <>
                {versionWarning && (
                  <Alert variant="warning" className="py-2 small mb-2">
                    This session was filled using template version{" "}
                    <strong>{versionWarning.storedVersion}</strong>, but that
                    version snapshot is no longer available. Showing the current
                    template instead — some fields may not match the original.
                  </Alert>
                )}
                <NoteRenderer
                  mode="fill"
                  pages={templatePages}
                  values={capturedValues}
                  onChange={(elementId, value) => {
                    setCapturedValues(prev => ({ ...prev, [elementId]: value }));
                  }}
                  onVoiceRequest={(elementId) => {
                    setVoiceTargetId(elementId);
                    setRecOpen(true);
                  }}
                />
              </>
            ) : selectedTemplateId ? (
              <p className="text-muted small">This template has no pages defined.</p>
            ) : null}
          </div>
          {localSection !== "write" && localSection !== "apptTemplate" && renderCurrentSection()}
        </div>
      </div>

      {/* Email Session Notes Modal */}
      <Modal show={showEmailForm} onHide={() => setShowEmailForm(false)} size="lg" centered>
        <Modal.Header
          closeButton
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0">
            <i className="fa-light fa-envelope me-2" />
            Email Session Notes
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-2 mb-2">
            <Col md={6}>
              <Form.Group className="position-relative">
                <Form.Label>To <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  autoComplete="off"
                  placeholder="Type a name, or enter email(s) separated by ;"
                  value={emailTo}
                  onChange={(e) => {
                    setEmailTo(e.target.value);
                    setToSuggestOpen(true);
                  }}
                  onFocus={() => setToSuggestOpen(true)}
                  onBlur={() => setTimeout(() => setToSuggestOpen(false), 150)}
                />
                {toSuggestions.length > 0 && (
                  <div
                    className="list-group position-absolute w-100 shadow-sm"
                    style={{ zIndex: 1056, maxHeight: "240px", overflowY: "auto" }}
                  >
                    {toSuggestions.map((p) => {
                      const emails = personEmails(p);
                      const extra = emails.length - 1;
                      return (
                        <button
                          key={p._id?.$oid || p._id || `${p.firstName}-${p.lastName}-${emails[0]}`}
                          type="button"
                          className="list-group-item list-group-item-action py-2"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handlePickPerson(p);
                          }}
                        >
                          <div className="fw-semibold text-truncate">
                            {personDisplayName(p) || emails[0]}
                          </div>
                          <div className="small text-muted text-truncate">
                            {emails[0]}
                            {extra > 0 && (
                              <span className="ms-1">
                                (+{extra} to CC)
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>CC</Form.Label>
                <Form.Control
                  type="text"
                  autoComplete="off"
                  placeholder="cc@example.com (separate multiple with ;)"
                  value={emailCc}
                  onChange={(e) => setEmailCc(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-2">
            <Form.Label>Subject</Form.Label>
            <Form.Control
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Message</Form.Label>
            <Form.Control
              as="textarea"
              rows={12}
              placeholder="The session notes PDF will be attached to this email."
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              style={{ minHeight: '14rem' }}
            />
          </Form.Group>
          <hr className="my-3" />
          <div className="mb-2 fw-bold small text-muted">Include in PDF</div>
          <PdfOptionCheckboxes
            options={pdfOptions}
            setOptions={setPdfOptions}
            templateTitle={pdfTemplateTitle}
            setTemplateTitle={setPdfTemplateTitle}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowEmailForm(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSendEmail} disabled={!emailTo.trim()}>
            <i className="fa-regular fa-envelope me-1" />Send Email
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Download Options Modal */}
      <Modal show={showDownloadOptions} onHide={() => setShowDownloadOptions(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Download Options</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2 fw-bold small text-muted">Include in PDF</div>
          <PdfOptionCheckboxes
            options={pdfOptions}
            setOptions={setPdfOptions}
            templateTitle={pdfTemplateTitle}
            setTemplateTitle={setPdfTemplateTitle}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDownloadOptions(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmDownload} disabled={pdfExporting || isSaving}>
            <i className="fa fa-download me-1" />Download
          </Button>
        </Modal.Footer>
      </Modal>

      <RecorderModal
        show={recOpen}
        onClose={closeRecorder}
        onDone={applyTranscriptToTarget}
        vr={vr}
      />
    </div>
  );

  return isFullScreen
    ? createPortal(sessionContent, document.body)
    : sessionContent;
};
